import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import {
  ProtocolPatientStatusDetail,
  ProtocolPatientStatusStatistics
} from '../../../models/Entities/Protocols/ProtocolPatientStatusStatistics';
import { PatientVisit } from '../../../models/Entities/Protocols/PatientVisist';
import {
  buildCompletedVisitTimes,
  buildMonthlyTimeline,
  VisitTimeline
} from '../../Contracts/statistics/VisitExecution.Functions';
import { statusStage } from '../../Contracts/statistics/PatientStatusStatistics.Functions';

/**
 * Estadísticas de la cartera de protocolos: todo lo que se puede decir del
 * conjunto con la lista de protocolos y las estadísticas de pacientes de todos
 * ellos, que la API entrega en un solo pedido.
 */

export interface CountItem {
  key: string;
  label: string;
  count: number;
}

/**
 * Cuenta protocolos por un atributo (estado, fase, especialidad, sponsor), de
 * mayor a menor. Los que no tienen el dato se agrupan en "Sin dato": dejarlos
 * afuera haría que las barras no sumen el total de protocolos.
 */
export const countBy = (
  protocols: Protocol[],
  getKey: (protocol: Protocol) => string | number | undefined | null,
  getLabel: (protocol: Protocol) => string | undefined | null,
  emptyLabel = 'Sin dato'
): CountItem[] => {
  const counts = new Map<string, CountItem>();
  protocols.forEach((protocol) => {
    const rawKey = getKey(protocol);
    const label = getLabel(protocol)?.trim();
    const key = rawKey === undefined || rawKey === null || rawKey === '' || !label ? '__none__' : String(rawKey);
    const current = counts.get(key) ?? { key, label: key === '__none__' ? emptyLabel : (label as string), count: 0 };
    current.count += 1;
    counts.set(key, current);
  });
  return Array.from(counts.values()).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'es'));
};

/**
 * Cuenta protocolos por el texto de un atributo, no por su id.
 *
 * Existe porque hay catálogos con entradas repetidas: dos especialidades con ids
 * distintos y la misma descripción se dibujarían como dos barras iguales. Se
 * agrupa por el texto normalizado —sin mayúsculas, tildes ni espacios de más— y se
 * muestra la primera forma en que aparece.
 */
export const countByLabel = (
  protocols: Protocol[],
  getLabel: (protocol: Protocol) => string | undefined | null,
  emptyLabel = 'Sin dato'
): CountItem[] =>
  countBy(
    protocols,
    (protocol) => normalize(getLabel(protocol)).replace(/\s+/g, ' ') || undefined,
    getLabel,
    emptyLabel
  );

/**
 * Nombre del sponsor. La API publica el campo como `laboratoy` (con la errata) y
 * el modelo del front lo declara `laboratory`: se leen los dos para no depender de
 * cuál de las dos formas llegue.
 */
export const sponsorName = (protocol: Protocol): string | undefined =>
  protocol.laboratory?.title ?? (protocol as any).laboratoy?.title ?? undefined;

/** Un protocolo cerrado o inactivo no está en curso. */
export const isOpenProtocol = (protocol: Protocol): boolean => protocol.active !== false && !protocol.closed;

/**
 * Protocolo en estado ongoing, con el mismo criterio que el listado de protocolos
 * (código o título del estado). El tablero de la cartera mira sólo estos: los demás
 * no aportan a la operación del día y multiplican los pedidos.
 */
export const isOngoingProtocol = (protocol: Protocol): boolean => {
  const code = (protocol.status?.code ?? '').toLowerCase();
  const title = (protocol.status?.title ?? '').toLowerCase();
  return code.includes('ongoing') || title.includes('ongoing') || title.includes('en curso');
};

export interface StageCounts {
  total: number;
  selection: number;
  screenFailure: number;
  randomized: number;
  unknown: number;
}

const emptyCounts = (): StageCounts => ({ total: 0, selection: 0, screenFailure: 0, randomized: 0, unknown: 0 });

/** Pacientes de un protocolo por etapa, con el mismo criterio de etapas que el tablero del protocolo. */
export const stageCountsOf = (statistics: ProtocolPatientStatusStatistics | undefined): StageCounts => {
  const counts = emptyCounts();
  (statistics?.statuses ?? []).forEach((item) => {
    counts[statusStage(item.status)] += item.count || 0;
  });
  counts.total = statistics?.total ?? counts.selection + counts.screenFailure + counts.randomized + counts.unknown;
  return counts;
};

const addCounts = (a: StageCounts, b: StageCounts): StageCounts => ({
  total: a.total + b.total,
  selection: a.selection + b.selection,
  screenFailure: a.screenFailure + b.screenFailure,
  randomized: a.randomized + b.randomized,
  unknown: a.unknown + b.unknown
});

/** Porcentaje entero, o null cuando no hay contra qué comparar. */
const toProgress = (value: number, objective: number): number | null =>
  objective > 0 ? Math.round((value / objective) * 100) : null;

export interface RecruitmentRow {
  protocolId: number;
  protocolNumber: string;
  title: string;
  sponsor?: string;
  status?: string;
  isOpen: boolean;
  objective: number;
  counts: StageCounts;
  /** Randomizados sobre el objetivo de pacientes, en entero; null sin objetivo cargado. */
  progress: number | null;
}

/**
 * Reclutamiento por protocolo: pacientes por etapa contra el objetivo. Se mide con
 * los randomizados, que son los que cuentan para el objetivo del estudio; los que
 * están en selección todavía pueden no entrar.
 *
 * Orden: primero los que tienen más randomizados, y a igualdad por número.
 */
export const buildRecruitmentRows = (
  protocols: Protocol[],
  statistics: ProtocolPatientStatusStatistics[]
): RecruitmentRow[] => {
  const byProtocol = new Map<number, ProtocolPatientStatusStatistics>();
  (statistics ?? []).forEach((item) => byProtocol.set(item.protocolId, item));

  return protocols
    .map((protocol) => {
      const counts = stageCountsOf(byProtocol.get(protocol.id));
      const objective = protocol.patientsObjective || 0;
      return {
        protocolId: protocol.id,
        protocolNumber: protocol.protocolNumber || `#${protocol.id}`,
        title: protocol.title || protocol.fullTitle || '',
        sponsor: sponsorName(protocol),
        status: protocol.status?.title,
        isOpen: isOpenProtocol(protocol),
        objective,
        counts,
        progress: toProgress(counts.randomized, objective)
      };
    })
    .sort(
      (a, b) =>
        b.counts.randomized - a.counts.randomized ||
        a.protocolNumber.localeCompare(b.protocolNumber, 'es', { numeric: true, sensitivity: 'base' })
    );
};

export interface PortfolioSummary {
  protocols: number;
  open: number;
  closed: number;
  sponsors: number;
  /** Suma de los objetivos de pacientes cargados. */
  objective: number;
  patients: StageCounts;
  /** Randomizados de toda la cartera sobre la suma de objetivos; null sin objetivos. */
  enrollment: number | null;
  /** Protocolos de la lista que todavía no tienen pacientes con estado. */
  protocolsWithoutPatients: number;
}

/**
 * Resumen de la cartera. Los pacientes se cuentan sólo de los protocolos de la
 * lista, para que el resumen y la tabla de reclutamiento digan lo mismo.
 */
export const buildPortfolioSummary = (
  protocols: Protocol[],
  statistics: ProtocolPatientStatusStatistics[]
): PortfolioSummary => {
  const rows = buildRecruitmentRows(protocols, statistics);
  const patients = rows.reduce((acc, row) => addCounts(acc, row.counts), emptyCounts());
  const objective = rows.reduce((acc, row) => acc + row.objective, 0);
  const sponsors = new Set(rows.map((row) => row.sponsor).filter((name): name is string => !!name));
  const open = rows.filter((row) => row.isOpen).length;

  return {
    protocols: rows.length,
    open,
    closed: rows.length - open,
    sponsors: sponsors.size,
    objective,
    patients,
    enrollment: toProgress(patients.randomized, objective),
    protocolsWithoutPatients: rows.filter((row) => row.counts.total === 0).length
  };
};

/** Minúsculas y sin tildes, para buscar "Oncologia" y encontrar "Oncología". */
const normalize = (value?: string | null): string =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/** Protocolos cuyo número, título o sponsor contienen el texto buscado. */
export const searchRecruitmentRows = (rows: RecruitmentRow[], searchText: string): RecruitmentRow[] => {
  const query = normalize(searchText);
  if (!query) return rows;
  return rows.filter(
    (row) =>
      normalize(row.protocolNumber).includes(query) ||
      normalize(row.title).includes(query) ||
      normalize(row.sponsor).includes(query)
  );
};

//#region Líneas de tiempo de la cartera

export interface ProtocolCount {
  protocolId: number;
  protocolNumber: string;
  count: number;
}

export interface PortfolioTimeline {
  timeline: VisitTimeline;
  /** Cantidad por protocolo, de mayor a menor. */
  byProtocol: ProtocolCount[];
}

export interface ProtocolVisits {
  protocolId: number;
  visits: PatientVisit[];
}

const toTimeValue = (value?: Date | string | null): number | undefined => {
  if (!value) return undefined;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? undefined : time;
};

const sortProtocolCounts = (
  counts: Map<number, { count: number; fallback?: string }>,
  protocols: Protocol[]
): ProtocolCount[] => {
  const numbers = new Map<number, string>();
  protocols.forEach((protocol) => numbers.set(protocol.id, protocol.protocolNumber));
  return Array.from(counts.entries())
    .map(([protocolId, value]) => ({
      protocolId,
      protocolNumber: numbers.get(protocolId) || value.fallback || `#${protocolId}`,
      count: value.count
    }))
    .sort(
      (a, b) =>
        b.count - a.count || a.protocolNumber.localeCompare(b.protocolNumber, 'es', { numeric: true, sensitivity: 'base' })
    );
};

/**
 * Randomizaciones por mes en toda la cartera, con el acumulado y el reparto por
 * protocolo.
 *
 * Cuenta a cada paciente con fecha de randomización, y también a los que están en
 * la etapa de randomizados sin fecha (esos van en "sin fecha", sin ubicarse en el
 * tiempo). Un paciente en selección o con falla de selección no fue randomizado.
 */
export const buildRandomizationTimeline = (
  details: ProtocolPatientStatusDetail[],
  protocols: Protocol[]
): PortfolioTimeline => {
  const times: (number | undefined)[] = [];
  const counts = new Map<number, { count: number; fallback?: string }>();

  (details ?? []).forEach((item) => {
    const time = toTimeValue(item.randomizationDate);
    if (time === undefined && statusStage(item.status) !== 'randomized') return;
    times.push(time);
    const current = counts.get(item.protocolId) ?? { count: 0, fallback: item.protocolNumber ?? undefined };
    current.count += 1;
    counts.set(item.protocolId, current);
  });

  return { timeline: buildMonthlyTimeline(times), byProtocol: sortProtocolCounts(counts, protocols) };
};

/**
 * Visitas completadas por mes en toda la cartera. Se arma con las visitas de cada
 * protocolo, pedidas por separado; los protocolos que todavía no llegaron
 * simplemente no suman.
 */
export const buildPortfolioVisitsTimeline = (items: ProtocolVisits[], protocols: Protocol[]): PortfolioTimeline => {
  const times: (number | undefined)[] = [];
  const counts = new Map<number, { count: number; fallback?: string }>();

  (items ?? []).forEach((item) => {
    const protocolTimes = buildCompletedVisitTimes(item.visits);
    if (protocolTimes.length === 0) return;
    times.push(...protocolTimes);
    counts.set(item.protocolId, { count: protocolTimes.length });
  });

  return { timeline: buildMonthlyTimeline(times), byProtocol: sortProtocolCounts(counts, protocols) };
};

//#endregion Líneas de tiempo de la cartera
